// Capa CENTRAL de presentación de errores. Traduce el sobre de la API ({error, detalle.codigo}) a un
// mensaje que un operador entiende, y CONSERVA el código técnico y la correlación para soporte.
// Una pantalla nunca muestra "HTTP 409": pregunta aquí.
import { ErrorApi } from '../api/cliente';

export type Severidad = 'error' | 'aviso' | 'bloqueo' | 'autorizacion';

export interface ErrorPresentable {
  titulo: string;
  mensaje: string;
  /** Código de dominio o categoría (lo que se dicta a soporte). */
  codigo: string;
  severidad: Severidad;
  status: number;
  correlacionId: string | null;
  /** Mensaje literal del backend (se muestra en el detalle técnico). */
  tecnico: string | null;
  /** Detalle estructurado que la pantalla puede usar (línea, estado DTE…). */
  detalle: Record<string, unknown>;
}

interface Entrada {
  titulo: string;
  mensaje?: string;
  severidad?: Severidad;
}

/** Códigos de dominio (detalle.codigo) → mensaje operacional. */
const POR_CODIGO: Record<string, Entrada> = {
  // caja · mesón (fases 1 y 2a)
  PAGO_EXCEDE_NOTA: {
    titulo: 'El pago supera el saldo',
    mensaje: 'Lo que se quiere cobrar supera lo que falta pagar de la nota. Revisa el monto.',
    severidad: 'bloqueo',
  },
  RECIBO_APLICADO: {
    titulo: 'Recibo ya aplicado',
    mensaje:
      'El recibo ya se aplicó a una factura o boleta: no se anula, se corrige con nota de crédito.',
    severidad: 'bloqueo',
  },
  TURNO_NO_ABIERTO: {
    titulo: 'Sin turno de caja abierto',
    mensaje:
      'Para cobrar en efectivo, tarjeta o cheque abre primero tu turno en Caja › Mi turno de caja. Una transferencia sí se puede cobrar sin turno.',
    severidad: 'bloqueo',
  },
  DOCUMENTO_SIN_PAGO: {
    titulo: 'La venta no tiene su pago',
    mensaje:
      'Para emitir el documento sin pago se necesita la autorización de un supervisor: cobra la venta (F10) o usa «Emitir sin pago» y pide la firma en línea.',
    severidad: 'bloqueo',
  },
  // 0122 (Rodrigo, 2026-10-27): emitir sin pago desde el punto de venta con firma en línea.
  DOCUMENTO_NO_MODIFICABLE: {
    titulo: 'El documento ya se eligió',
    mensaje:
      'La nota ya tiene un documento emitido: lo que falta sale con el mismo tipo de documento.',
    severidad: 'bloqueo',
  },
  AUTORIZACION_SIN_PAGO_USADA: {
    titulo: 'La autorización ya se usó',
    mensaje: 'Esa autorización para emitir sin pago ya sirvió para un documento. Pide otra.',
    severidad: 'bloqueo',
  },
  SIN_PAGO_NO_PENDIENTE: {
    titulo: 'Ya no está pendiente',
    mensaje: 'Otra persona ya resolvió o retiró esta solicitud. Se actualiza la lista.',
    severidad: 'aviso',
  },
  NOTA_PAGADA: {
    titulo: 'La nota ya está pagada',
    mensaje: 'No hace falta autorización: emite el documento (F10).',
    severidad: 'aviso',
  },
  TURNO_YA_ABIERTO: {
    titulo: 'Ya tienes un turno abierto',
    mensaje: 'Cierra tu turno de caja actual antes de abrir otro.',
    severidad: 'aviso',
  },
  // Sin mensaje fijo: el del servidor dice qué caja es y que se comparte con «Compartir caja».
  PUNTO_OCUPADO: { titulo: 'La caja ya está abierta', severidad: 'aviso' },
  // Sin mensaje fijo: el del servidor dice de quién es la caja o qué turno la ocupa.
  CAJA_PERSONAL_AJENA: { titulo: 'Caja personal de otra persona', severidad: 'aviso' },
  // 0133: sin caja abierta, el efectivo y la tarjeta piden abrir o compartir la caja del puesto.
  SIN_CAJA_ABIERTA: { titulo: 'Abre o comparte la caja de tu puesto', severidad: 'aviso' },
  TESORERIA_OCUPADA: { titulo: 'La caja de tesorería la tiene otra persona', severidad: 'bloqueo' },
  CAJA_TESORERIA: { titulo: 'Caja de tesorería', severidad: 'aviso' },
  PUESTO_OCUPADO: { titulo: 'Ese puesto ya tiene caja', severidad: 'aviso' },
  YA_COMPARTE: { titulo: 'Ya compartes esa caja', severidad: 'aviso' },
  RETIRO_EXCEDE_EFECTIVO: {
    titulo: 'Retiro mayor que el efectivo',
    mensaje: 'El retiro supera el efectivo que debería haber en la gaveta.',
    severidad: 'bloqueo',
  },
  TURNO_CERRADO: {
    titulo: 'Turno cerrado',
    mensaje: 'El turno de caja ya se cerró: abre uno nuevo para seguir cobrando.',
    severidad: 'aviso',
  },
  TURNO_AJENO: {
    titulo: 'Turno de otro cajero',
    mensaje: 'Solo el cajero del turno puede registrar retiros o cerrarlo.',
    severidad: 'autorizacion',
  },
  TURNO_NO_REVISABLE: {
    titulo: 'Turno no revisable',
    mensaje: 'Solo se revisa un turno cerrado y que todavía no tenga revisión.',
    severidad: 'aviso',
  },
  REVISION_PROPIA: {
    titulo: 'No puedes revisar tu propio turno',
    mensaje: 'El cierre de tu turno lo revisa otra persona (control por oposición).',
    severidad: 'autorizacion',
  },
  RECIBO_DE_TURNO_CERRADO: {
    titulo: 'Recibo de un turno cerrado',
    mensaje:
      'El recibo pertenece a un turno de caja ya cerrado y arqueado: anularlo descuadraría el cierre. Lo corrige tesorería.',
    severidad: 'bloqueo',
  },
  // stock y reservas
  STOCK_INSUFICIENTE: {
    titulo: 'Stock insuficiente',
    mensaje: 'No hay stock disponible suficiente en la ubicación elegida.',
    severidad: 'bloqueo',
  },
  STOCK_INSUFICIENTE_RESERVA_TOTAL: {
    titulo: 'Stock insuficiente para reservar todo',
    mensaje:
      'La política de este canal exige reservar la nota completa y no hay disponible suficiente. La nota no se confirmó.',
    severidad: 'bloqueo',
  },
  RESERVA_INSUFICIENTE_DESPACHO: {
    titulo: 'Reserva insuficiente para despachar',
    mensaje: 'Lo que se quiere despachar supera lo reservado para la nota en esa ubicación.',
    severidad: 'bloqueo',
  },
  RESERVA_INSUFICIENTE_REVISION: {
    titulo: 'Reserva insuficiente',
    mensaje: 'La revisión pide más de lo que se puede reservar; la versión vigente se mantiene.',
    severidad: 'bloqueo',
  },
  POLITICA_RESERVA_FALTANTE: {
    titulo: 'Canal sin política de reserva',
    mensaje: 'Este canal no tiene política de reserva vigente. Avise a administración comercial.',
    severidad: 'bloqueo',
  },
  NADA_QUE_DESPACHAR: {
    titulo: 'Nada que despachar',
    mensaje: 'No queda cantidad reservada pendiente de despacho en esa ubicación.',
    severidad: 'aviso',
  },
  DESPACHO_EXCEDE_NOTA: {
    titulo: 'Cantidad mayor a la pendiente',
    mensaje: 'La cantidad a despachar supera lo pendiente de la nota.',
    severidad: 'bloqueo',
  },
  UBICACION_NO_DESPACHA: {
    titulo: 'Ubicación no despacha',
    mensaje: 'Desde esa ubicación no se despacha una venta (solo bodega, mesón o camión).',
  },
  MODALIDAD_UBICACION: {
    titulo: 'Modalidad y ubicación no calzan',
    mensaje: 'La venta en ruta sale de un camión, y desde un camión solo se entrega en ruta.',
  },
  // precio
  PRECIO_NO_DISPONIBLE: {
    titulo: 'Hay líneas sin precio',
    mensaje:
      'Una o más líneas no tienen precio de lista ni precio manual autorizado. La nota no se confirma sin precio (nunca $0).',
    severidad: 'bloqueo',
  },
  PRECIO_MANUAL_SIN_PERMISO: {
    titulo: 'Precio manual no autorizado',
    mensaje:
      'Su usuario no puede fijar precios manuales. Quite el precio manual o pida autorización.',
    severidad: 'autorizacion',
  },
  PRECIO_BAJO_COSTO: {
    titulo: 'Precio bajo el costo',
    mensaje:
      'El precio manual deja la línea bajo el costo del producto. Ponga un precio igual o mayor al mínimo indicado.',
    severidad: 'bloqueo',
  },
  PRECIO_MANUAL_INVALIDO: {
    titulo: 'Precio manual inválido',
    mensaje: 'El precio manual debe ser mayor que cero.',
  },
  MOTIVO_OBLIGATORIO: { titulo: 'Falta el motivo', mensaje: 'Esta acción exige un motivo.' },
  DESCUENTO_EXCEDE: {
    titulo: 'Descuento excesivo',
    mensaje: 'El descuento supera el importe de la línea.',
  },
  DESCUENTO_INVALIDO: {
    titulo: 'Descuento inválido',
    mensaje: 'El descuento porcentual va de 0 a 100.',
  },
  ESCALA_FUERA_DE_BASE: {
    titulo: 'Escala mal definida',
    mensaje:
      'La presentación tiene escalas propias; por D-50 las escalas van en la presentación base. Avise a precios.',
    severidad: 'bloqueo',
  },
  DOCUMENTO_NO_DEFINIDO: {
    titulo: 'Documento no definido',
    mensaje: 'Elija factura o boleta: el canal no tiene documento por defecto.',
  },
  CLASE_NO_SOPORTADA: {
    titulo: 'Producto no vendible aquí',
    mensaje: 'Ese tipo de producto (p. ej. envase retornable) todavía no se vende por este flujo.',
  },
  // crédito y aprobaciones
  REQUIERE_AUTORIZACION: {
    titulo: 'Esta venta requiere autorización',
    severidad: 'autorizacion',
  },
  SIN_FUENTE_CXC: {
    titulo: 'Esta venta requiere autorización',
    mensaje:
      'No hay una fuente confiable de cuenta corriente del cliente; la venta con cupo la autoriza una persona con permiso de crédito.',
    severidad: 'autorizacion',
  },
  CREDITO_RECHAZADO: {
    titulo: 'Crédito rechazado',
    mensaje: 'La política de crédito rechaza esta venta (ver motivos). La nota quedó como estaba.',
    severidad: 'bloqueo',
  },
  FIRMANTE_SIN_PERMISO_DEL_AMBITO: {
    titulo: 'No puede firmar este ámbito',
    mensaje: 'Aprobar este ámbito exige un permiso que su usuario no tiene.',
    severidad: 'autorizacion',
  },
  AUTOAPROBACION_PROHIBIDA: {
    titulo: 'No puede aprobar su propia solicitud',
    mensaje: 'La política exige que apruebe otra persona (segregación).',
    severidad: 'autorizacion',
  },
  APROBACION_DESACTUALIZADA: {
    titulo: 'La solicitud cambió',
    mensaje: 'La nota cambió después de pedir la aprobación. Recargue.',
  },
  SIN_SOLICITUD_PENDIENTE: {
    titulo: 'Sin solicitud pendiente',
    mensaje: 'No hay aprobación pendiente.',
  },
  // DTE (D-132): la salida la decide el backend; aquí solo se explica en palabras.
  TRASLADO_SIN_DTE: {
    titulo: 'Mercadería no autorizada para salida: documento tributario pendiente',
    mensaje:
      'La línea no tiene un documento tributario que ampare el traslado. Solicite el documento y espere su envío al SII.',
    severidad: 'bloqueo',
  },
  DTE_NO_ENVIADO_ANTES_DEL_TRASLADO: {
    titulo: 'Mercadería no autorizada para salida: documento tributario pendiente',
    mensaje:
      'El documento tributario aún no cumple las condiciones para autorizar la salida de mercadería (debe estar enviado al SII y no rechazado).',
    severidad: 'bloqueo',
  },
  DTE_INTERACTIVO_TRASLADO_PENDIENTE: {
    titulo: 'Mercadería no autorizada para salida: documento tributario pendiente',
    mensaje: 'El documento tributario de la venta todavía se está enviando.',
    severidad: 'bloqueo',
  },
  EXCEPCION_SIN_COBERTURA_NO_HABILITADA: {
    titulo: 'Excepción no habilitada',
    mensaje: 'La salida sin cobertura de datos no está habilitada.',
    severidad: 'bloqueo',
  },
  DTE_PENDIENTE: {
    titulo: 'Documento tributario pendiente',
    mensaje: 'El documento tributario todavía se está procesando.',
    severidad: 'aviso',
  },
  DTE_PENDIENTE_ENVIO: {
    titulo: 'Documento tributario pendiente de envío',
    mensaje: 'El documento está firmado y espera su envío al SII.',
    severidad: 'aviso',
  },
  DTE_ESPERANDO_SII: {
    titulo: 'Esperando al SII',
    mensaje: 'El documento fue enviado y el SII todavía no responde. Se consulta solo.',
    severidad: 'aviso',
  },
  DTE_RECHAZADO: {
    titulo: 'Documento tributario rechazado',
    mensaje:
      'El SII rechazó el documento. Avise a administración: se corrige con un documento nuevo.',
    severidad: 'bloqueo',
  },
  DTE_REQUIERE_CONCILIACION: {
    titulo: 'Documento en conciliación',
    mensaje:
      'No se sabe con certeza si el SII recibió el documento. Se concilia antes de reenviar: no lo reintente a mano.',
    severidad: 'bloqueo',
  },
  DTE_CONFLICTO_SII: {
    titulo: 'El SII informa que los datos no coinciden',
    mensaje:
      'El SII informa que los datos no coinciden. Requiere revisión: lo resuelve una persona autorizada.',
    severidad: 'bloqueo',
  },
  CONFLICTO_DTE_SII: {
    titulo: 'El SII informa que los datos no coinciden',
    mensaje:
      'El SII informa que los datos no coinciden. Requiere revisión: lo resuelve una persona autorizada.',
    severidad: 'bloqueo',
  },
  DTE_YA_ENVIADO: {
    titulo: 'Documento ya enviado',
    mensaje: 'El documento ya salió al SII: se corrige con nota de crédito o débito.',
    severidad: 'bloqueo',
  },
  NOTA_NO_CONFIRMADA: {
    titulo: 'La nota no está confirmada',
    mensaje: 'Solo una nota confirmada se documenta o despacha.',
  },
  NADA_QUE_DOCUMENTAR: {
    titulo: 'Nada que documentar',
    mensaje: 'Todas las líneas de la nota ya tienen su documento tributario.',
    severidad: 'aviso',
  },
  LINEA_YA_DOCUMENTADA: {
    titulo: 'Línea ya documentada',
    mensaje: 'La línea ya tiene un documento tributario vigente.',
    severidad: 'aviso',
  },
  EMISOR_NO_CONFIGURADO: {
    titulo: 'Empresa sin emisor tributario',
    mensaje:
      'La empresa no tiene configurado su emisor de documentos tributarios. Avise a administración.',
    severidad: 'bloqueo',
  },
  RECEPTOR_INCOMPLETO: {
    titulo: 'Datos del cliente incompletos',
    mensaje:
      'La factura exige RUT, razón social, giro y dirección tributaria con comuna. Complétalos en la ficha del cliente (Alt+F) y vuelve a grabar la venta, o emite boleta (Alt+T).',
    severidad: 'bloqueo',
  },
  TIPO_DTE_NO_HABILITADO: {
    titulo: 'Tipo de documento no habilitado',
    mensaje: 'La empresa no tiene habilitado ese tipo de documento tributario.',
    severidad: 'bloqueo',
  },
  CAF_NO_DISPONIBLE: {
    titulo: 'Sin folios disponibles',
    mensaje:
      'No hay folios autorizados vigentes para ese tipo de documento. Avise a administración.',
    severidad: 'bloqueo',
  },
  DESPACHO_DESACTUALIZADO: {
    titulo: 'El despacho quedó desactualizado',
    mensaje: 'La nota cambió después de preparar el despacho. Descártelo y prepare uno nuevo.',
    severidad: 'aviso',
  },
  DESPACHO_SIN_LINEAS: {
    titulo: 'Despacho sin líneas',
    mensaje: 'Indique al menos una línea con cantidad a despachar.',
  },
  UBICACION_INACTIVA: {
    titulo: 'Ubicación inactiva',
    mensaje: 'La ubicación de origen no está activa.',
  },
  // concurrencia y ciclo de vida
  VERSION_OBSOLETA: {
    titulo: 'Otro usuario cambió este registro',
    mensaje: 'Lo que ve está desactualizado. Recargue y repita la acción.',
    severidad: 'aviso',
  },
  TRANSICION_ILEGAL: {
    titulo: 'Acción no válida en este estado',
    mensaje: 'El documento ya no está en un estado que permita esta acción. Recargue.',
    severidad: 'aviso',
  },
  REVISION_PENDIENTE: {
    titulo: 'Hay una revisión esperando firma',
    mensaje: 'Resuelva primero la revisión propuesta.',
  },
  IDEMPOTENCY_KEY_OBLIGATORIA: {
    titulo: 'Error interno de la interfaz',
    mensaje: 'La acción se envió sin clave de idempotencia. Avise a soporte.',
  },
  PERMISO_DENEGADO: { titulo: 'Acción no autorizada', severidad: 'autorizacion' },
};

/** Categorías (`error`) cuando no hay código de dominio. */
const POR_CATEGORIA: Record<string, Entrada> = {
  no_autenticado: { titulo: 'Sesión no válida', severidad: 'autorizacion' },
  permiso_denegado: {
    titulo: 'Acción no autorizada',
    mensaje: 'Su usuario no tiene permiso para esta acción en la empresa activa.',
    severidad: 'autorizacion',
  },
  validacion: { titulo: 'Datos inválidos' },
  no_encontrado: {
    titulo: 'No encontrado',
    mensaje: 'El registro no existe o no es visible en esta empresa.',
  },
  conflicto: { titulo: 'No se pudo completar', severidad: 'bloqueo' },
  estado_invalido: { titulo: 'Estado no válido', severidad: 'bloqueo' },
  no_implementado: { titulo: 'Función no disponible todavía' },
  sin_conexion: {
    titulo: 'Sin conexión',
    mensaje:
      'No hay conexión con el servidor. Nada se guardó; reintente cuando vuelva la conexión.',
  },
  interno: {
    titulo: 'Error del servidor',
    mensaje: 'El servidor falló. Dicte a soporte el código de correlación.',
  },
};

export function presentarError(e: unknown): ErrorPresentable {
  if (e instanceof ErrorApi) {
    const detalle = (e.cuerpo.detalle ?? {}) as Record<string, unknown>;
    const codigo = e.codigo;
    const entrada = POR_CODIGO[codigo] ??
      POR_CATEGORIA[e.cuerpo.error] ?? {
        titulo: 'No se pudo completar',
      };
    let mensaje = entrada.mensaje ?? e.cuerpo.mensaje;
    if (e.cuerpo.error === 'permiso_denegado' && typeof detalle['permiso'] === 'string')
      mensaje = `${POR_CATEGORIA['permiso_denegado']!.mensaje} (permiso: ${detalle['permiso']}).`;
    return {
      titulo: entrada.titulo,
      mensaje,
      codigo,
      severidad: entrada.severidad ?? 'error',
      status: e.status,
      correlacionId: e.correlacionId,
      tecnico: e.cuerpo.mensaje,
      detalle,
    };
  }
  return {
    titulo: 'Error inesperado de la interfaz',
    mensaje: e instanceof Error ? e.message : String(e),
    codigo: 'ERROR_INTERFAZ',
    severidad: 'error',
    status: 0,
    correlacionId: null,
    tecnico: null,
    detalle: {},
  };
}

/** Motivos de crédito (D-108) en palabras del operador. */
export const MOTIVOS_CREDITO: Record<string, string> = {
  CLIENTE_BLOQUEADO: 'Cliente bloqueado',
  CONDICION_NO_PERMITIDA: 'Condición de pago no permitida',
  PLAZO_EXCEDIDO: 'Plazo mayor al permitido',
  SIN_LINEA: 'Cliente sin línea de crédito',
  SIN_FUENTE_CXC: 'Esta venta requiere autorización (sin fuente de cuenta corriente)',
  DEUDA_VENCIDA: 'Deuda vencida (morosidad)',
  SOBRECUPO: 'Supera el cupo',
  SIN_POLITICA_CREDITO: 'Sin política de crédito configurada',
  CREDITO_SOBRE_CREDITO: 'Crédito especial con factura pendiente (sin crédito sobre crédito)',
  COBRO_CONTRA_ENTREGA: 'Cobra lo pendiente al entregar',
};

/** Ámbitos de aprobación en palabras del operador (quién firma lo exige el backend). */
export const NOMBRE_AMBITO: Record<string, string> = {
  NOTA_VENTA: 'Nota de venta',
  DESCUENTO: 'Descuento',
  PRECIO_MANUAL: 'Precio manual',
  CREDITO: 'Excepción de crédito',
  FLETE_VENTA: 'Flete de venta',
  DEVOLUCION_CLIENTE: 'Devolución',
  NOTA_CREDITO: 'Nota de crédito',
  NOTA_DEBITO: 'Nota de débito',
  LISTA_PRECIO: 'Cambio de lista de precios',
  SIN_PAGO: 'Documento sin pago',
};
